import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, KeyRound, ShieldCheck, Smartphone, Bell, BellOff, Phone, MessageCircle, Mail, MapPin, Globe, BadgeCheck, GraduationCap, Building2, Send } from "lucide-react";
import { initials } from "@/mobile/mobileUtils";
import { enablePush, disablePush, currentSubscription, pushSupported, isStandalone, permissionState, sendTestPush } from "@/mobile/push";
import { toast } from "sonner";

export default function MobileAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const changePw = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try { await api.put(`/staff/${user.id}`, { password: pw }); toast.success("Password updated"); setPw(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const doLogout = async () => { await logout(); nav("/m/login", { replace: true }); };

  return (
    <div className="p-4 space-y-5" data-testid="mobile-account">
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 rounded-full bg-secondary text-white text-lg font-bold flex items-center justify-center">{initials(user?.name)}</div>
        <div>
          <div className="font-head font-extrabold text-lg text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-500">@{user?.username} · <span className="capitalize">{user?.role}</span></div>
        </div>
      </div>

      <PushCard />

      <OfficeContactCard />

      <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><KeyRound className="h-4 w-4 text-primary" /> Change my password</div>
        <div className="flex gap-2">
          <input data-testid="account-new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
          <button onClick={changePw} disabled={saving} data-testid="account-save-password" className="bg-primary text-white px-4 rounded-xl text-sm font-semibold disabled:opacity-60">{saving ? "..." : "Save"}</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Smartphone className="h-4 w-4 text-primary" /> Install this app</div>
        <p className="text-xs text-slate-500 leading-relaxed">On iPhone: tap the <b>Share</b> icon in Safari, then <b>“Add to Home Screen”</b>. It opens full-screen like a native app.</p>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" /> Chahal Overseas CRM · secure staff access</div>

      <button onClick={doLogout} data-testid="account-logout" className="w-full flex items-center justify-center gap-2 border border-rose-200 text-rose-600 rounded-2xl py-3 font-semibold"><LogOut className="h-4 w-4" /> Sign out</button>
    </div>
  );
}

function PushCard() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);
  const supported = pushSupported();
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const needsInstall = isIOS && !isStandalone();

  useEffect(() => { currentSubscription().then((s) => { setOn(!!s); setChecked(true); }); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); toast.success("Notifications turned off on this device"); }
      else { await enablePush(); setOn(true); toast.success("Notifications are on for this device"); }
    } catch (e) { toast.error(e?.message || apiErr(e)); } finally { setBusy(false); }
  };

  const test = async () => {
    try { await sendTestPush(); toast.success("Test notification sent"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3" data-testid="account-push-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            {on ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-slate-400" />} Lock-screen alerts
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Get @mentions, direct messages, urgent items and new case or task assignments pushed to this phone.
          </p>
        </div>
        <button onClick={toggle} disabled={busy || !supported || !checked} data-testid="account-push-toggle"
          className={`shrink-0 w-14 h-8 rounded-full transition-colors duration-200 disabled:opacity-40 ${on ? "bg-primary" : "bg-slate-200"}`}>
          <span className={`block h-6 w-6 bg-white rounded-full shadow transition-transform duration-200 ${on ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>

      {!supported && <p className="text-[11px] text-amber-600" data-testid="account-push-unsupported">This browser can't show push notifications.</p>}
      {supported && needsInstall && !on && (
        <p className="text-[11px] text-amber-600" data-testid="account-push-install-hint">
          Add this app to your Home Screen first (Share → Add to Home Screen), then open it from the icon to turn alerts on.
        </p>
      )}
      {supported && permissionState() === "denied" && (
        <p className="text-[11px] text-rose-600" data-testid="account-push-blocked">
          Notifications are blocked for this app in iPhone Settings → Notifications.
        </p>
      )}
      {on && (
        <button onClick={test} data-testid="account-push-test" className="text-xs font-semibold text-primary inline-flex items-center gap-1.5">
          <Send className="h-3.5 w-3.5" /> Send me a test notification
        </button>
      )}
    </div>
  );
}

const telHref = (n) => `tel:${String(n || "").replace(/[^\d+]/g, "")}`;
const waHref = (n) => `https://wa.me/${String(n || "").replace(/\D/g, "")}`;

function OfficeContactCard() {
  const [co, setCo] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setCo(r.data?.company || {})).catch(() => setCo({})); }, []);
  if (!co) return null;

  const rows = [
    co.phone && { key: "phone", icon: Phone, label: "Main line", value: co.phone, href: telHref(co.phone) },
    co.office_phone && { key: "office_phone", icon: Building2, label: "Office", value: co.office_phone, href: telHref(co.office_phone) },
    co.study_permit_phone && { key: "study_permit_phone", icon: GraduationCap, label: "Study permits", value: co.study_permit_phone, href: telHref(co.study_permit_phone) },
    co.whatsapp && { key: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: co.whatsapp, href: waHref(co.whatsapp) },
    co.email && { key: "email", icon: Mail, label: "Email", value: co.email, href: `mailto:${co.email}` },
    co.website && { key: "website", icon: Globe, label: "Website", value: co.website.replace(/^https?:\/\//, ""), href: co.website },
    co.address && { key: "address", icon: MapPin, label: "Office address", value: co.address, href: `https://maps.apple.com/?q=${encodeURIComponent(co.address)}` },
  ].filter(Boolean);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="account-contact-card">
      <div className="px-4 pt-4 pb-2">
        <div className="font-head font-bold text-slate-900 text-sm">{co.name || "Chahal Overseas Immigration"}</div>
        {co.rcic && <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><BadgeCheck className="h-3.5 w-3.5 text-primary" /> {co.rcic}</div>}
      </div>
      <div className="divide-y divide-slate-50">
        {rows.map(({ key, icon: Icon, label, value, href }) => (
          <a key={key} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
            data-testid={`contact-${key}`} className="flex items-center gap-3 px-4 py-3 active:bg-slate-50">
            <span className="h-9 w-9 rounded-xl bg-slate-50 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></span>
            <span className="min-w-0">
              <span className="block text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
              <span className="block text-sm font-medium text-slate-800 truncate">{value}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
